import type { ModeId } from './contract.js';
import { THEMES } from './registry.js';

/**
 * The driver app's first-load theme (Warren, 21 October 2026):
 *   - a driver's own saved choice always wins;
 *   - with no saved choice, the app follows the device's light or dark setting;
 *   - when the device reports no preference, it opens in light, never dark;
 *   - the first paint is already that mode, with no dark flash.
 * ThemeProvider (followSystemMode) and the inline script below decide the same way, so what the browser paints before
 * React mounts is what the app then renders.
 *
 * Only an explicit choice is saved, under its own key: the old `evi.mode` key was written on every first load with the
 * default, so it cannot tell a choice from a default.
 */
export const DRIVER_MODE_CHOICE_KEY = 'evi.mode.chosen';

type MatchMedia = (query: string) => { matches: boolean };
/** The device's setting: dark only when the device says dark; light otherwise, and when it says nothing. */
export function systemMode(matchMedia: MatchMedia | undefined): ModeId {
  try { return matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'; } catch { return 'light'; }
}

/** Per family: the modes it offers, and the page background and text it paints in each, from the registry. */
export function firstPaintColors() {
  return Object.fromEntries(Object.entries(THEMES).map(([id, t]) => [id, {
    modes: t.availableModes ?? (['light', 'dark'] as ModeId[]),
    light: { bg: t.variants.light.tokens.colors.bg, text: t.variants.light.tokens.colors.text },
    dark: { bg: t.variants.dark.tokens.colors.bg, text: t.variants.dark.tokens.colors.text },
  }]));
}

/**
 * A classic inline script for the driver app's <head>. It runs before the first paint: picks the family (query, then
 * saved) and the mode (query, then the saved choice, then the device setting, light when none), clamps the mode to the
 * family's modes, and paints the page background, text, colour scheme and status-bar colour. Each storage or media read
 * is guarded, so a blocked localStorage or an old browser still ends in light, never dark.
 */
export function firstPaintScript(keys: { theme?: string; mode?: string } = {}): string {
  const themeKey = JSON.stringify(keys.theme ?? 'evi.theme'), modeKey = JSON.stringify(keys.mode ?? DRIVER_MODE_CHOICE_KEY);
  return `(function(){var P=${JSON.stringify(firstPaintColors())},w=window,d=document.documentElement,f='default',m=null,s=null;`
    + `try{s=w.localStorage}catch(e){}`
    + `try{var q=new URLSearchParams(w.location.search),qf=q.get('theme'),qm=q.get('mode');`
    + `if(qf&&P.hasOwnProperty(qf))f=qf;else{var sf=s&&s.getItem(${themeKey});if(sf&&P.hasOwnProperty(sf))f=sf}`
    + `if(qm==='light'||qm==='dark')m=qm;else{var sm=s&&s.getItem(${modeKey});if(sm==='light'||sm==='dark')m=sm}}catch(e){}`
    + `if(!m){try{m=w.matchMedia&&w.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light'}catch(e){m='light'}}`
    + `var t=P[f];if(t.modes.indexOf(m)<0)m=t.modes[0];var c=t[m];`
    + `d.setAttribute('data-theme',f);d.setAttribute('data-mode',m);d.style.colorScheme=m;`
    + `d.style.setProperty('--evi-bg',c.bg);d.style.setProperty('--evi-text',c.text);d.style.backgroundColor=c.bg;d.style.color=c.text;`
    + `var x=document.querySelector('meta[name="theme-color"]');if(x)x.setAttribute('content',c.bg)})();`;
}
